import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ProblemaButton from "@/components/ProblemaButton";
import { Iniciales } from "@/components/marca/Layout";
import { IdiomaSelector } from "@/components/IdiomaSelector";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { hoyEn } from "@/lib/chofer";
import { Truck, Fuel, PackageSearch, Ellipsis, HelpCircle, KeyRound, LogOut, Compass } from "lucide-react";
import { useGuia } from "@/components/guia/Guia";
import { t } from "@/lib/i18n"; // Prompt M

// Fix Q (2026-10-13): toda la app del chofer en inglés. «Report a problem» va al final de cada pantalla.
export default function ChoferLayout() {
  const { user, logout } = useAuth();
  const { iniciar: iniciarGuia } = useGuia(); // Prompt M
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [masOpen, setMasOpen] = useState(false);
  const [resumen, setResumen] = useState("");

  useEffect(() => {
    api.post("/chofer/jornada/abrir").catch(() => {});
  }, []);

  const tabs = [
    { to: "/chofer", label: t("Loads"), icon: Truck, end: true, testid: "chofer-nav-cargas" },
    { to: "/chofer/diesel", label: t("Fuel"), icon: Fuel, testid: "ir-diesel-btn" },
    { to: "/chofer/insumos", label: t("Supplies"), icon: PackageSearch, testid: "ir-insumos-btn" },
  ];
  const ir = (to) => { setMasOpen(false); navigate(to); };

  return (
    <div className="chofer-app min-h-screen bg-paper">
      {pathname === "/chofer" && (
        <header className="bg-navy border-t-4 border-brand border-b-[3px] border-b-gold mb-4" data-testid="chofer-header">
          <div className="px-4 pt-4 pb-4 max-w-md mx-auto flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-display font-bold text-[12.5px] uppercase tracking-[.12em] text-white" data-testid="chofer-marca">{t("LEONA’S GROUP · DRIVER {v0}", { v0: user?.nombre })}</p>
              <h1 className="mt-1 font-display font-bold text-[26px] leading-[1.05] text-white" data-testid="chofer-fecha">{hoyEn()}</h1>
              {resumen !== "" && <p className="text-[#C4D0E0] text-[14px] mt-1" data-testid="chofer-resumen">{resumen === 1 ? t("1 load today") : t("{n} loads today", { n: resumen })}</p>}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <IdiomaSelector claro />
              <Iniciales nombre={user?.nombre} size={38} className="!bg-white/15 !text-white" />
            </div>
          </div>
        </header>
      )}
      <div className="pb-24">
        <Outlet context={{ setResumen }} />
        <ProblemaButton />
      </div>

      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-line" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="chofer-bottom-bar">
        <div className="max-w-md mx-auto grid grid-cols-4">
          {tabs.map((tt) => (
            <NavLink key={tt.to} to={tt.to} end={tt.end} data-testid={tt.testid}
              className={({ isActive }) => cn("flex flex-col items-center justify-center gap-1 h-16 text-[12px] font-semibold", isActive ? "text-navy-2" : "text-ink-3")}>
              {({ isActive }) => (<><tt.icon className="h-[22px] w-[22px]" strokeWidth={isActive ? 2.4 : 1.8} />{tt.label}</>)}
            </NavLink>
          ))}
          <button data-testid="chofer-mas-btn" onClick={() => setMasOpen(true)} className="flex flex-col items-center justify-center gap-1 h-16 text-[12px] font-semibold text-ink-3">
            <Ellipsis className="h-[22px] w-[22px]" strokeWidth={1.8} />{t("More")}</button>
        </div>
      </nav>

      <Sheet open={masOpen} onOpenChange={setMasOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" style={{ paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
          <SheetHeader><SheetTitle>{t("More")}</SheetTitle></SheetHeader>
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between h-12 px-3 rounded-md border border-line" data-testid="chofer-idioma-fila">
              <span className="text-sm font-medium">{t("Language / Idioma")}</span>
              <IdiomaSelector />
            </div>
            <Button data-testid="chofer-guia-btn" variant="outline" className="w-full h-12 justify-start" onClick={() => { setMasOpen(false); setTimeout(() => iniciarGuia(), 350); }}><Compass className="h-5 w-5" />{t(" App guide")}</Button>
            <Button data-testid="chofer-ayuda-btn" variant="outline" className="w-full h-12 justify-start" onClick={() => ir("/chofer/ayuda")}><HelpCircle className="h-5 w-5" />{t(" Help")}</Button>
            <Button data-testid="chofer-cambiar-password-btn" variant="outline" className="w-full h-12 justify-start" onClick={() => ir("/cambiar-password")}><KeyRound className="h-5 w-5" />{t(" Change password")}</Button>
            <Button data-testid="chofer-logout" variant="outline" className="w-full h-12 justify-start text-danger" onClick={logout}><LogOut className="h-5 w-5" />{t(" Sign out")}</Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
